import React, { useEffect, useState } from "react";
import { growthApi } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Plus, Trash2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const statusColor = {
  Available: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Limited: "bg-amber-100 text-amber-700 border-amber-200",
  Waitlist: "bg-red-100 text-red-700 border-red-200",
};
const staffingColor = {
  STAFFABLE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  "STAFFING GAP": "bg-red-100 text-red-700 border-red-200",
  "OWNER REVIEW": "bg-slate-100 text-slate-600 border-slate-200",
};

const TIME_FLAGS = ["morning", "afternoon", "evening", "weekend"];

const AvailabilityPanel = () => {
  const { toast } = useToast();
  const [data, setData] = useState({ records: [], statuses: [], staffingStatuses: [] });
  const [draft, setDraft] = useState({ county: "", service: "", status: "Limited", staffingStatus: "OWNER REVIEW" });

  const load = () => growthApi.availabilityList().then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.county.trim() || !draft.service.trim()) return;
    try {
      const res = await growthApi.availabilityCreate(draft);
      setData((d) => ({ ...d, records: [...d.records, res.data] }));
      setDraft({ county: "", service: "", status: "Limited", staffingStatus: "OWNER REVIEW" });
    } catch {
      toast({ title: "Could not add record", variant: "destructive" });
    }
  };

  const patch = async (id, changes) => {
    setData((d) => ({ ...d, records: d.records.map((r) => (r.id === id ? { ...r, ...changes } : r)) }));
    try { await growthApi.availabilityUpdate(id, changes); } catch { load(); }
  };

  const remove = async (id) => {
    try {
      await growthApi.availabilityRemove(id);
      setData((d) => ({ ...d, records: d.records.filter((r) => r.id !== id) }));
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  return (
    <div data-testid="availability-panel" className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <h3 className="font-display font-bold text-[#0E2A33]">Current Availability & Staffing Demand</h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          De-identified staffing map — never display client names publicly. Public site always says:
          "Referral coverage is available statewide. Actual service availability depends on staffing, location,
          schedule, and individual support requirements."
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div><Label className="text-xs">County</Label><Input data-testid="avail-county-input" value={draft.county} onChange={(e) => setDraft({ ...draft, county: e.target.value })} className="mt-1 h-9" placeholder="Franklin" /></div>
          <div><Label className="text-xs">Service</Label><Input data-testid="avail-service-input" value={draft.service} onChange={(e) => setDraft({ ...draft, service: e.target.value })} className="mt-1 h-9" placeholder="HPC" /></div>
          <div>
            <Label className="text-xs">Availability</Label>
            <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v })}>
              <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{data.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Staffing Status</Label>
            <Select value={draft.staffingStatus} onValueChange={(v) => setDraft({ ...draft, staffingStatus: v })}>
              <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{data.staffingStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="avail-add-btn" onClick={add} className="bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full h-9"><Plus className="mr-1 h-4 w-4" /> Add</Button>
        </div>
      </div>

      <div className="space-y-2">
        {data.records.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-100 shadow-sm px-4 py-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[#0E2A33]">{r.county} County — {r.service}</span>
                <Badge className={`border text-[11px] ${statusColor[r.status]}`}>{r.status}</Badge>
                <Badge className={`border text-[11px] ${staffingColor[r.staffingStatus]}`}>{r.staffingStatus}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Select value={r.status} onValueChange={(v) => patch(r.id, { status: v })}>
                  <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{data.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={r.staffingStatus} onValueChange={(v) => patch(r.id, { staffingStatus: v })}>
                  <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{data.staffingStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="icon" variant="outline" className="h-8 w-8 text-red-600 border-red-200" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2 flex-wrap text-xs text-slate-600">
              {TIME_FLAGS.map((f) => (
                <label key={f} className="flex items-center gap-1.5 capitalize cursor-pointer">
                  <Checkbox checked={!!r[f]} onCheckedChange={(v) => patch(r.id, { [f]: !!v })} /> {f}
                </label>
              ))}
              <label className="flex items-center gap-1.5 cursor-pointer">
                <Checkbox checked={!!r.transportationAvailable} onCheckedChange={(v) => patch(r.id, { transportationAvailable: !!v })} /> Transportation available
              </label>
              <Input value={r.notes || ""} onChange={(e) => patch(r.id, { notes: e.target.value })} className="h-7 text-xs flex-1 min-w-[180px]" placeholder="Notes (e.g. 20–30 weekly hours, evenings)" />
            </div>
          </div>
        ))}
        {data.records.length === 0 && (
          <div className="text-center text-slate-400 py-10 bg-white rounded-2xl border border-slate-100">
            No availability records yet. Add county + service rows above as demand arrives.
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityPanel;
